import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import {
  DisplayServiceGroupDetail,
  RequestPatchTaskCompanyServiceGroup,
  RequestPostTaskCompanyServiceGroup,
  ResponseGetTaskCompanyServiceGroup,
} from '../../../../states/types/task'
import {
  createTaskCompanyServiceGroup,
  fetchTaskCompanyServiceGroup,
  fetchTaskCompanyServiceGroupDetail,
  fetchTaskLabelingTargetModeSetting,
  updateTaskCompanyServiceGroup,
  updateTaskLabelingTargetModeSetting,
} from '../../../../states/containers/task/taskSlice'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import EditTargetApplicationModal from '../../../components/task/EditTargetApplicationModal'
import { useAlert } from 'react-alert'
import { compile } from '../../../../utils/search'
import { updateTaskTargetSettingDisplayType } from '../../../../states/containers/common/currentSlice'
import { format, secToHour, secToMinute, secToSec } from '../../../../utils/date'

const SORT_TYPE = [
  {
    type: "time_desc",
    label: "直近一週間の使用時間順",
  },
  {
    type: "new",
    label: "新着順",
  },
  {
    type: "old",
    label: "登録順",
  },
  {
    type: "app_asc",
    label: "アプリ名昇順",
  },
  {
    type: "app_desc",
    label: "アプリ名降順",
  },
]

const SLICE_LIMIT = 100

const DISPLAY_LIMIT = 10

const MULTI_EDIT_LIMIT = 500

const Index: FC = () => {
  const {
    task,
    current,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()
  const alert = useAlert()

  const initDisplayNameDetail: DisplayServiceGroupDetail = {
    id: 0,
    name: "data",
    labeling_target: false,
    icon: "fas fa-desktop",
    color: "#333333",
    created_at: "",
    time: 0,
    named: false,
    company_service_names: [
      {
        id: 1,
        service_type: 'application',
        raw_name: "data",
      },
    ],
  }

  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [data, setData] = useState<ResponseGetTaskCompanyServiceGroup>({ company_service_groups: [] })
  const [initData, setInitData] = useState<ResponseGetTaskCompanyServiceGroup>({ company_service_groups: [] })
  const [filterType, setFilterType] = useState<string>("all")
  const [isDisplayEditModal, setIsDisplayEditModal] = useState<boolean>(false)
  const [multiCheckIdList, setMultiCheckIdList] = useState<number[]>([])
  const [displayNameDetails, setDisplayNameDetails] = useState<DisplayServiceGroupDetail[]>([initDisplayNameDetail])
  const [searchWord, setSearchWord] = useState<string>("")
  const [windowTitles, setWindowTitles] = useState<string[]>([])
  const [sortType, setSortType] = useState<string>("time_desc")
  const [modified, setModified] = useState<string>("")

  const [sliceNumber, setSliceNumber] = useState<number>(0)

  const [isLabelingTargetModeLoading, setIsLabelingTargetModeLoading] = useState<boolean>(false)

  const [isCreateLoading, setIsCreateLoading] = useState<boolean>(false)

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const slicedData = useMemo(() => {
    const length = Math.ceil(data.company_service_groups.length / SLICE_LIMIT)
    return new Array(length).fill(null).map((_, i) => data.company_service_groups.slice(i * SLICE_LIMIT, (i + 1) * SLICE_LIMIT))
  }, [data])

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = () => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if ((bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setSliceNumber((self) => self + 1)
    }
  }

  const toggleLabelingTarget = (companyServiceName: string, companyServiceGroupIds: number[], isTarget: boolean) => {
    dispatch(
      updateTaskCompanyServiceGroup(
        {
          ids: companyServiceGroupIds,
          labeling_target: isTarget,
        },
        () => {
          alert.success(`[${companyServiceName}]を${isTarget ? "設定" : "解除"}しました`)
          const changedValue = initData?.company_service_groups.map((v) => (
            companyServiceGroupIds.includes(v.id) ? { ...v, labeling_target: isTarget } : v
          ))
          if (changedValue) setInitData({ company_service_groups: changedValue })
        },
      ),
    )
  }

  const editTargetApplication = (param: DisplayServiceGroupDetail[]) => {
    setDisplayNameDetails(param)
    setIsDisplayEditModal(true)
    if (param.map((v) => v.id).length === 1) {
      dispatch(
        fetchTaskCompanyServiceGroupDetail(
          { id: param.map((v) => v.id)[0].toString() },
          (result) => {
            setWindowTitles(result.sample_window_titles)
          },
        ),
      )
    }
  }

  const modTargetService = (params: RequestPatchTaskCompanyServiceGroup) => {
    setIsButtonLoading(true)
    dispatch(
      updateTaskCompanyServiceGroup(
        params,
        () => {
          alert.success("変更が完了しました")
          setIsCreateLoading(true)
          setMultiCheckIdList([])
          dispatch(
            fetchTaskCompanyServiceGroup(
              (result) => {
                setInitData(result)
                setIsCreateLoading(false)
                setIsButtonLoading(false)
              },
            ),
          )
          setModified(params?.name || "")
          const targetEl = document.getElementById(`app_${params?.name || ""}`)
          targetEl?.scrollIntoView({ behavior: 'smooth', block: "center" })
          setTimeout(() => {
            setModified("")
          }, 5000)
        },
      ),
    )
  }

  const addTargetService = (params: RequestPostTaskCompanyServiceGroup) => {
    setIsButtonLoading(true)
    dispatch(
      createTaskCompanyServiceGroup(
        params,
        () => {
          alert.success("変更が完了しました")
          setIsCreateLoading(true)
          setMultiCheckIdList([])
          dispatch(
            fetchTaskCompanyServiceGroup(
              (result) => {
                setInitData(result)
                setIsCreateLoading(false)
                setIsButtonLoading(false)
              },
            ),
          )
          setModified(params?.name || "")
          const targetEl = document.getElementById(`app_${params?.name || ""}`)
          targetEl?.scrollIntoView({ behavior: 'smooth', block: "center" })
          setTimeout(() => {
            setModified("")
          }, 5000)
        },
      ),
    )
  }

  const clickCheckbox = (groupId: number) => {
    if (multiCheckIdList.includes(groupId)) {
      setMultiCheckIdList((cur) => cur.filter((v) => v !== groupId))
    } else {
      setMultiCheckIdList((cur) => [...cur, groupId])
    }
  }

  const clickCheckboxAll = () => {
    if (multiCheckIdList.length === data.company_service_groups.length) {
      setMultiCheckIdList([])
    } else {
      setMultiCheckIdList(data.company_service_groups.map((v) => v.id))
    }
  }

  const changeMultiLabelingTarget = (isTarget: boolean) => {
    setIsButtonLoading(true)
    const companyServiceGroupIds = initData.company_service_groups.filter((v) => multiCheckIdList.includes(v.id))
      .map((v) => v.id)
    dispatch(
      updateTaskCompanyServiceGroup(
        {
          ids: companyServiceGroupIds,
          labeling_target: isTarget,
        },
        () => {
          alert.success("変更が完了しました")
          setIsButtonLoading(false)
          setMultiCheckIdList([])
          const changedValue = initData?.company_service_groups.map((v) => (
            companyServiceGroupIds.includes(v.id) ? { ...v, labeling_target: isTarget } : v
          ))
          if (changedValue) setInitData({ company_service_groups: changedValue })
        },
      ),
    )
  }

  const changeFilterType = (type: string) => {
    setFilterType(type)
    dispatch(updateTaskTargetSettingDisplayType(type))
  }

  const closeModal = () => {
    setIsDisplayEditModal(false)
    setWindowTitles([])
  }

  const toggleLabelingTargetMode = (targetMode: boolean) => {
    setIsLabelingTargetModeLoading(true)
    dispatch(
      updateTaskLabelingTargetModeSetting(
        { default_labeling_target: targetMode },
        () => {
          if (targetMode) {
            alert.success("仕分け対象アプリを自動設定に変更しました")
          } else {
            alert.success("仕分け対象アプリを手動設定に変更しました")
          }
          setIsLabelingTargetModeLoading(false)
        },
      ),
    )
  }

  const buildSecToTime = (sec: number) => (
    <>
      {
        !!Number(secToHour(sec)) && (
          <>
            {`${secToHour(sec)}時間`}
          </>
        )
      }
      {
        Number(secToMinute(sec)) ? (
          <>
            {`${secToMinute(sec)}分`}
          </>
        ) : (
          <>
            0分
          </>
        )
      }
      {
        !!Number(secToSec(sec)) && (
          <>
            {`${secToSec(sec)}秒`}
          </>
        )
      }
    </>
  )

  useEffect(() => {
    if (!data) return
    const word = searchWord.toLowerCase().split(/\s+/)
    let list = initData.company_service_groups.filter((value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLowerCase()))
    })
    if (filterType === "target") {
      list = list.filter((v) => v.labeling_target)
    } else if (filterType === "no_target") {
      list = list.filter((v) => !v.labeling_target)
    }
    if (sortType === "new") {
      list = list.sort((a, b) => Number(format(b.created_at, "YYYYMMDDHms")) - Number(format(a.created_at, "YYYYMMDDHms")))
    } else if (sortType === "old") {
      list = list.sort((a, b) => Number(format(b.created_at, "YYYYMMDDHms")) - Number(format(a.created_at, "YYYYMMDDHms"))).reverse()
    } else if (sortType === "time_asc") {
      list = list.sort((a, b) => a.time - b.time)
    } else if (sortType === "time_desc") {
      list = list.sort((a, b) => a.time - b.time).reverse()
    } else if (sortType === "app_asc") {
      list = list.sort((a, b) => a.name.localeCompare(b.name, 'ja'))
    } else if (sortType === "app_desc") {
      list = list.sort((a, b) => a.name.localeCompare(b.name, 'ja')).reverse()
    }
    setData({ company_service_groups: list })
    setMultiCheckIdList([])
    setSliceNumber(0)
  }, [filterType, searchWord, initData, sortType])

  useEffect(() => {
    dispatch(
      fetchTaskCompanyServiceGroup(
        (result) => {
          setInitData(result)
          setPageLoading(false)
        },
      ),
    )
    dispatch(fetchTaskLabelingTargetModeSetting())
  }, [])

  useEffect(() => {
    setFilterType(current.taskTargetSettingDisplayType)
  }, [current.taskTargetSettingDisplayType])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 仕分け対象アプリ設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        仕分け対象アプリ設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          仕分け対象アプリ設定
          {/* <VideoGuide
            videoId="vpAXoNKucBE"
          /> */}
        </h1>
      </section>
      <section className="page_description">
        <p>
          仕分け対象アプリ設定を設定します。
        </p>
        <p>
          ここで設定したアプリケーションのデータが計測の対象になります。データを計測したいときは、対象になるアプリケーションを設定してください。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              pageLoading ? (
                <LoadingSquare>
                  <>
                    仕分け対象アプリ設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div
                    className="header_search_area"
                    style={{
                      border: "solid 1px #CCCCCC",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div className="detail">
                      新しく追加される仕分け対象アプリを自動設定にする
                      {
                        isLabelingTargetModeLoading ? (
                          <FontAwesomeIcon icon={['fas', 'spinner']} spin style={{ fontSize: "24px" }} />
                        ) : (
                          <FontAwesomeIcon
                            icon={['fas', task.taskLabelingTargetModeSetting?.default_labeling_target ? 'toggle-on' : 'toggle-off']}
                            style={{
                              color: task.taskLabelingTargetModeSetting?.default_labeling_target ? "#008800" : "#666666",
                              cursor: "pointer",
                              fontSize: "24px",
                            }}
                            onClick={() => toggleLabelingTargetMode(!task.taskLabelingTargetModeSetting?.default_labeling_target)}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="header_search_area">
                    <div className="detail">
                      表示:
                      <div className="toggle_area">
                        <button
                          type="button"
                          className={`${filterType === "all" ? "on" : "off"}`}
                          onClick={() => changeFilterType("all")}
                        >
                          すべて
                        </button>
                        <button
                          type="button"
                          className={`${filterType === "target" ? "on" : "off"}`}
                          onClick={() => changeFilterType("target")}
                        >
                          仕分け対象アプリのみ
                        </button>
                        <button
                          type="button"
                          className={`${filterType === "no_target" ? "on" : "off"}`}
                          onClick={() => changeFilterType("no_target")}
                        >
                          非仕分け対象アプリのみ
                        </button>
                      </div>
                    </div>
                    <div className="detail">
                      アプリケーション名検索:
                      <input
                        type="text"
                        value={searchWord}
                        onChange={(e) => setSearchWord(e.target.value)}
                      />
                      {
                        !!searchWord.length && (
                          <button
                            type="button"
                            className="reset"
                            onClick={() => setSearchWord("")}
                            style={{
                              position: "absolute",
                              right: "6px",
                              top: "50%",
                              fontSize: "10px",
                              padding: 0,
                              color: "#999999",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <FontAwesomeIcon icon={['far', 'times-circle']} />
                          </button>
                        )
                      }
                    </div>
                    <div className="detail">
                      並び順:
                      <select
                        onChange={(e) => setSortType(e.target.value)}
                        style={{
                          appearance: "auto",
                          border: "solid 1px #999999",
                          borderRadius: "4px",
                          padding: "6px 8px",
                        }}
                      >
                        {
                          SORT_TYPE.map((v) => (
                            <option
                              key={`sort_${v.type}`}
                              value={v.type}
                            >
                              {v.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  <div
                    style={{
                      color: "#4372ba",
                      fontSize: "14px",
                      fontWeight: "bold",
                      border: "dashed 1px #4372ba",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "16px",
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'info-circle']} />
                    これまで使用したことがあるアプリケーションが表示されています
                  </div>
                  <div
                    style={{
                      border: `solid 1px ${multiCheckIdList.length ? "#228b22" : "#999999"}`,
                      borderRadius: "4px",
                      backgroundColor: multiCheckIdList.length ? "#f3fff9" : "#EEEEEE",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      gap: "12px",
                      marginBottom: "16px",
                      position: "sticky",
                      top: "60px",
                      zIndex: "1",
                    }}
                  >
                    {
                      multiCheckIdList.length ? (
                        <>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "2px",
                            }}
                          >
                            <div
                              style={{
                                color: "#228b22",
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {multiCheckIdList.length}
                            </div>
                            <div>
                              件選択されています
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            選択したアプリケーションを
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "4px 8px",
                              }}
                            >
                              {
                                multiCheckIdList.length <= MULTI_EDIT_LIMIT ? (
                                  <button
                                    type="button"
                                    // eslint-disable-next-line max-len
                                    onClick={() => editTargetApplication(initData.company_service_groups.filter((v) => multiCheckIdList.includes(v.id)))}
                                    style={{
                                      borderRadius: "4px",
                                      color: "#FFFFFF",
                                      backgroundColor: "#4371ba",
                                    }}
                                    disabled={isButtonLoading}
                                  >
                                    {isButtonLoading && (<FontAwesomeIcon icon={['fas', 'spinner']} spin />)}
                                    まとめて編集する
                                  </button>
                                ) : (
                                  <Tips
                                    description={`まとめて編集する場合、${MULTI_EDIT_LIMIT}件以下の選択にしてください`}
                                    className=""
                                    icon={false}
                                  >
                                    <button
                                      type="button"
                                      style={{
                                        borderRadius: "4px",
                                        color: "#999999",
                                        backgroundColor: "#DDDDDD",
                                        cursor: "not-allowed",
                                      }}
                                    >
                                      まとめて編集する
                                    </button>
                                  </Tips>
                                )
                              }
                              <button
                                type="button"
                                onClick={() => changeMultiLabelingTarget(true)}
                                style={{
                                  borderRadius: "4px",
                                  color: "#FFFFFF",
                                  backgroundColor: "#027916",
                                }}
                                disabled={isButtonLoading}
                              >
                                {isButtonLoading && (<FontAwesomeIcon icon={['fas', 'spinner']} spin />)}
                                仕分け対象アプリに設定する
                              </button>
                              <button
                                type="button"
                                onClick={() => changeMultiLabelingTarget(false)}
                                style={{
                                  borderRadius: "4px",
                                  color: "#FFFFFF",
                                  backgroundColor: "#999999",
                                }}
                                disabled={isButtonLoading}
                              >
                                {isButtonLoading && (<FontAwesomeIcon icon={['fas', 'spinner']} spin />)}
                                仕分け対象アプリから解除する
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              marginTop: "8px",
                            }}
                          >
                            選択されていません
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            選択したアプリケーションを
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                backgroundColor: "#EEEEEE",
                                padding: "4px 8px",
                              }}
                            >
                              <button
                                type="button"
                                style={{
                                  borderRadius: "4px",
                                  color: "#999999",
                                  backgroundColor: "#DDDDDD",
                                  cursor: "not-allowed",
                                }}
                              >
                                まとめて編集する
                              </button>
                              <button
                                type="button"
                                style={{
                                  borderRadius: "4px",
                                  color: "#999999",
                                  backgroundColor: "#DDDDDD",
                                  cursor: "not-allowed",
                                }}
                              >
                                仕分け対象アプリに設定する
                              </button>
                              <button
                                type="button"
                                style={{
                                  borderRadius: "4px",
                                  color: "#999999",
                                  backgroundColor: "#DDDDDD",
                                  cursor: "not-allowed",
                                }}
                              >
                                仕分け対象アプリから解除する
                              </button>
                            </div>
                          </div>
                        </>
                      )
                    }
                  </div>
                  <div className="company_service_name_table">
                    {
                      isCreateLoading && (
                        <div
                          style={{
                            backgroundColor: "#FFFFFFe6",
                            position: "absolute",
                            width: "100%",
                            minHeight: "100%",
                            zIndex: "1",
                          }}
                        >
                          <LoadingSquare style={{ position: "sticky", top: "50%" }}>
                            <>
                              データ表示中…
                            </>
                          </LoadingSquare>
                        </div>
                      )
                    }
                    <div className="row_head">
                      <div>
                        <Tips
                          description="仕分け対象アプリを設定・解除します"
                          className=""
                        >
                          <>
                            対象
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="名称の変更や対象の変更をおこないます"
                          className=""
                        >
                          <>
                            編集
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="業務仕分けボードに表示されるアプリケーション名です"
                          className=""
                        >
                          <>
                            対象アプリケーション名
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="アプリケーションとして表示される対象のプロセス・ドメイン名一覧です"
                          className=""
                        >
                          <>
                            対象プロセス・ドメイン名
                          </>
                        </Tips>
                      </div>
                      <div>
                        <Tips
                          description="直近一週間で使用したアプリケーションの合計時間です"
                          className=""
                        >
                          <>
                            直近一週間の使用時間
                          </>
                        </Tips>
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(50% - 5px)",
                          left: "-47px",
                          transform: "translateY(-50%)",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={() => clickCheckboxAll()}
                        role="none"
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#999999",
                            userSelect: "none",
                          }}
                        >
                          全選択
                        </div>
                        <FontAwesomeIcon
                          icon={['far', multiCheckIdList.length === data.company_service_groups.length ? 'check-square' : 'square']}
                          style={{
                            backgroundColor: "#ffffff",
                            color: multiCheckIdList.length === data.company_service_groups.length ? "#0f9655" : "#666666",
                            fontSize: "18px",
                          }}
                        />
                      </div>
                    </div>
                    {
                      slicedData.filter((_w, i) => i <= sliceNumber).flat().map((v) => (
                        <div
                          className={`row ${modified === v.name && "modified"}`}
                          key={v.name}
                          id={`app_${v.name}`}
                          style={{
                            backgroundColor: multiCheckIdList.includes(v.id) ? "#f0fff8" : "#FFFFFF",
                            position: "relative",
                          }}
                        >
                          <div>
                            <FontAwesomeIcon
                              icon={['fas', v.labeling_target ? 'toggle-on' : 'toggle-off']}
                              style={{
                                color: v.labeling_target ? "#008800" : "#666666",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleLabelingTarget(v.name, [v.id], !v.labeling_target)}
                            />
                          </div>
                          <div>
                            <FontAwesomeIcon
                              icon={['fas', 'cog']}
                              style={{
                                color: "#666666",
                                cursor: "pointer",
                              }}
                              onClick={() => editTargetApplication([v])}
                            />
                          </div>
                          <div
                            onClick={() => clickCheckbox(v.id)}
                            role="none"
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                wordBreak: "break-all",
                              }}
                            >
                              {
                                v.icon.split(' ').length === 2 && (
                                  <FontAwesomeIcon
                                    icon={[v.icon.split(' ')[0] as IconPrefix, v.icon.split(' ')[1] as IconName]}
                                    style={{
                                      color: v.color,
                                      fontSize: "16px",
                                    }}
                                    fixedWidth
                                  />
                                )
                              }
                              {v.name}
                              {
                                !v.named && (
                                  <Tips
                                    description="アプリケーション名を設定し忘れていませんか？"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'exclamation']}
                                        style={{
                                          color: "#ff0000",
                                          fontSize: "16px",
                                          cursor: "pointer",
                                        }}
                                        onClick={
                                          (e) => {
                                            editTargetApplication([v])
                                            e.stopPropagation()
                                          }
                                        }
                                      />
                                    </>
                                  </Tips>
                                )
                              }
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#666666",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              {
                                v.company_service_names.filter((_w, i) => i < DISPLAY_LIMIT).map((w) => (
                                  <div
                                    key={`${w.raw_name}_${w.service_type}`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: w.service_type === "application" ? "#ad0505" : "#067ecd",
                                        color: "#FFFFFF",
                                        borderRadius: "4px",
                                        fontSize: "10px",
                                        padding: "1px 4px",
                                        fontWeight: "initial",
                                        wordBreak: "keep-all",
                                      }}
                                    >
                                      {w.service_type === "application" ? "プロセス名" : "ドメイン名"}
                                    </div>
                                    {w.raw_name}
                                  </div>
                                ))
                              }
                              {
                                v.company_service_names.length > DISPLAY_LIMIT && (
                                  <div
                                    style={{
                                      textAlign: "left",
                                    }}
                                  >
                                    {`…他${v.company_service_names.length - DISPLAY_LIMIT}件`}
                                  </div>
                                )
                              }
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              {buildSecToTime(v.time)}
                            </div>
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "-30px",
                              fontSize: "18px",
                              cursor: "pointer",
                              transform: "translateY(-50%)",
                              border: "none",
                            }}
                            onClick={() => clickCheckbox(v.id)}
                            role="none"
                          >
                            <FontAwesomeIcon
                              icon={['far', multiCheckIdList.includes(v.id) ? 'check-square' : 'square']}
                              style={{ color: multiCheckIdList.includes(v.id) ? "#228b22" : "#666666" }}
                            />
                          </div>
                        </div>
                      ))
                    }
                    {
                      slicedData.length - 1 > sliceNumber && (
                        <>
                          <LoadingSquare>
                            <>
                              仕分け対象アプリデータを表示中…
                            </>
                          </LoadingSquare>
                        </>
                      )
                    }
                    {
                      !data.company_service_groups.length && (
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#999999",
                            padding: "32px",
                          }}
                        >
                          該当のアプリケーションがありません
                        </div>
                      )
                    }
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <EditTargetApplicationModal
        appList={initData.company_service_groups}
        details={displayNameDetails}
        closeCallback={closeModal}
        modCallback={(params) => modTargetService(params)}
        addCallback={(params) => addTargetService(params)}
        isDisplay={isDisplayEditModal}
        windowTitles={windowTitles}
      />
    </section>
  )
}

export default Index
